import {
    Component,
    Input,
    OnChanges,
    ViewChild,
    ElementRef,
    AfterViewInit
} from '@angular/core';
import Chart, {
    ChartData,
    ChartOptions,
    ChartDataSets,
    ChartConfiguration
} from 'chart.js';

import { ChartType } from '@enums/chart-type.enum';
import { UiService } from '@services/ui.service';
import { FormatService } from '@services/format.service';
import { ReportDataType } from '@enums/report-data-type.enum';
import { ReportVariation } from '@classes/report/report-variation';

@Component({
    selector: 'app-report-chart',
    templateUrl: './report-chart.component.html',
    styleUrls: ['./report-chart.component.scss']
})
export class ReportChartComponent implements AfterViewInit, OnChanges {
    @Input() variation: ReportVariation;
    @Input() showLegend = true;
    @ViewChild('canvas', { static: true }) canvas: ElementRef;

    colors: string[];

    chart: Chart;

    stacksLabels: string[];
    hasMultiStack: boolean;
    hasMultiDatasets: boolean;
    chartType = ChartType;

    constructor(
        private uiSrv: UiService,
        private formatSrv: FormatService
    ) { }

    ngAfterViewInit() {
        this.setChart();
    }

    ngOnChanges() {
        this.setChart();
    }

    private setChart(): void {
        this.colors = this.uiSrv.getChartsColors();

        if(!this.chart) {
            this.chart = new Chart(this.canvas.nativeElement, this.getChartConfig());
        } else {
            this.chart.data = this.dataFromReportItem();
            this.chart.update(0);
        }
    }

    private getChartConfig(): ChartConfiguration {
        const data = this.dataFromReportItem();

        switch(this.variation.displayType) {
            case ChartType.doughnut:
                return {
                    type: 'doughnut',
                    data: data,
                    options: this.setFlatChartOptions()
                };
            case ChartType.bar:
                return {
                    type: 'bar',
                    data: data,
                    options: this.setAxesChartOptions(true, true, true)
                };
            case ChartType.horizontalBar:
                return {
                    type: 'horizontalBar',
                    data: data,
                    options: this.setAxesChartOptions(true, true, false)
                };
            case ChartType.line:
                return {
                    type: 'line',
                    data: data,
                    options: this.setAxesChartOptions(false, true, true)
                };
            case ChartType.radar:
                return {
                    type: 'radar',
                    data: data
                };
        }
    }

    /**
     * Configure datasets and colors based on report items
     */
    private dataFromReportItem(): ChartData {
        const labels: string[] = this.variation.labels;
        this.hasMultiStack = this.variation.stacks.length > 1;
        this.stacksLabels = [];

        const datasets: ChartDataSets[] = this.variation.stacks.reduce((accumulator, stack, stackIndex) => {
            this.hasMultiDatasets = stack.datasets.length > 1;

            if(this.hasMultiStack && this.hasMultiDatasets) {
                this.stacksLabels.push(stack.name);
            }

            const dataset = stack.datasets.map((d, i) => {
                let colorIndex = i;
                let bgColor: string | string[];
                let borderColor = '#FFFFFF';
                let borderWidth = 1;

                // use stack index when there are only one dataset
                if(!this.hasMultiDatasets) {
                    colorIndex = stackIndex;
                }

                switch(this.variation.displayType) {
                    case ChartType.doughnut:
                        bgColor = this.colors;
                        break;
                    case ChartType.bar:
                    case ChartType.horizontalBar:
                        bgColor = this.colors[colorIndex];
                        break;
                    case ChartType.line:
                    case ChartType.radar:
                        borderColor = this.colors[colorIndex];
                        bgColor = 'transparent';
                        borderWidth = 3;
                        break;
                }

                return {
                    label: d.label,
                    data: d.data,
                    stack: stack.name,
                    borderColor: borderColor,
                    backgroundColor: bgColor,
                    borderWidth: borderWidth,
                    pointBackgroundColor: borderColor,
                    pointBorderColor: borderColor
                };
            });
            return [ ...accumulator, ...dataset];
        }, []);

        return {
            labels: labels,
            datasets: datasets
        };
    }

    private setFlatChartOptions(): ChartOptions {
        return {
            tooltips: {
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset: ChartDataSets = data.datasets[tooltipItem.datasetIndex];
                        const dataValues: number[] = <number[]>dataset.data;
                        const value: number = dataValues[tooltipItem.index];

                        if(this.variation.dataType === ReportDataType.amount) {
                            const total: number = dataValues.reduce((tot, val) => tot + val, 0);
                            return `${data.labels[tooltipItem.index]}: ${this.formatSrv.formatPercent(value / total)}`;
                        } else {
                            return this.formatSrv.formatPercent(<number>value);
                        }
                    }
                }
            },
            legend: {
                position: 'right',
                labels: {
                    generateLabels: (chart) => {
                        return chart.data.labels.map((label, i) => {
                            const value: number = chart.data.datasets[0].data[i] as number;
                            const text = `${label}: ${this.formatValue(value)}`;

                            return {
                                text: text,
                                fillStyle: chart.data.datasets[0].backgroundColor[i],
                                hidden: !chart.isDatasetVisible(0),
                                lineCap: chart.data.datasets[0].borderCapStyle,
                                lineDash: chart.data.datasets[0].borderDash,
                                lineDashOffset: chart.data.datasets[0].borderDashOffset,
                                lineJoin: chart.data.datasets[0].borderJoinStyle,
                                lineWidth: chart.data.datasets[0].borderWidth,
                                strokeStyle: chart.data.datasets[0].borderColor[i],
                                pointStyle: chart.data.datasets[0].pointStyle,
                            };
                        });
                    }
                }
            }
        };
    }

    private setAxesChartOptions(doesBeginAtZero = true, isStacked = true, isVertical = true): ChartOptions {
        this.setAxesLabels();

        const yAxes = [{
            ticks: {
                beginAtZero: doesBeginAtZero,
                callback: (value: number) => {
                    return this.formatValue(value);
                }
            },
            scaleLabel: {
                display: true,
                labelString: this.variation.axesLabels.data
            },
            stacked: isStacked
        }];
        const xAxes = [{
            scaleLabel: {
                display: true,
                labelString: this.variation.axesLabels.categories
            },
            stacked: isStacked,
            ticks: {
                autoSkip: false,
            }
        }];

        return {
            maintainAspectRatio: false,
            tooltips: {
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset: ChartDataSets = data.datasets[tooltipItem.datasetIndex];
                        const value: number = dataset.data[tooltipItem.index] as number;
                        const formattedValue = this.formatValue(value);

                        if(!this.showLegend) {
                            return formattedValue;
                        }

                        const labels = [];

                        if(dataset.stack) {
                            labels.push(dataset.stack);
                        }
                        if(dataset.label) {
                            labels.push(dataset.label);
                        }

                        return `${labels.join(' - ')}: ${formattedValue}`;
                    }
                }
            },
            legend: {
                display: this.showLegend,
                labels: {
                    generateLabels: (chart) => {
                        const filteredDatasets: ChartDataSets[] = chart.data.datasets.filter((dataset) => {
                            if(this.hasMultiStack && this.hasMultiDatasets) {
                                return dataset.stack === chart.data.datasets[0].stack;
                            }

                            return true;
                        });

                        const returnedLabels = filteredDatasets.map((dataset, i) => {
                            const labels = [];

                            // stackLabels empty if only one stack OR only one dataset
                            if(!this.stacksLabels.length && dataset.stack) {
                                labels.push(dataset.stack);
                            }

                            if(dataset.label) {
                                labels.push(dataset.label);
                            }

                            return {
                                text: labels.join(' - '),
                                fillStyle: dataset.backgroundColor,
                                hidden: !chart.isDatasetVisible(i),
                                lineCap: dataset.borderCapStyle,
                                lineDash: dataset.borderDash,
                                lineDashOffset: dataset.borderDashOffset,
                                lineJoin: dataset.borderJoinStyle,
                                lineWidth: dataset.borderWidth,
                                strokeStyle: dataset.borderColor,
                                pointStyle: dataset.pointStyle,
                            };
                        });

                        return returnedLabels;
                    }
                },
                onClick: () => {}
            },
            scales: {
                yAxes: isVertical ? yAxes : xAxes,
                xAxes: isVertical ? xAxes : yAxes
            }
        };
    }

    /**
     * Configure Axes labels for old reportItems compatibility
     * TODO: Remove it when rework's done on simu items
     */
    private setAxesLabels(): void {
        if(!this.variation.axesLabels) {
            this.variation.axesLabels = {
                categories: this.uiSrv.translate('reports.barChart.years.shortLabel'),
                data: this.uiSrv.translate(
                    this.variation.dataType === ReportDataType.amount ?
                    'reports.barChart.amounts.shortLabel'
                    : 'reports.barChart.percents.shortLabel')
            };
        }
    }

    private formatValue(value: number): string {
        if(this.variation.dataType === ReportDataType.amount) {
            if(Math.abs(value) < 10) {
                return this.formatSrv.formatAmount(value, 'fixed', 1) + " €";
            }

            if(Math.abs(value) < 1000) {
                return this.formatSrv.formatAmount(value);
            }

            return this.formatSrv.formatAmount(value, 'fixed', 0.001) + " K€";
        }

        return this.formatSrv.formatPercent(value);
    }
}
